import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

interface AnchorNavProps {
  sections: { id: string; label: string }[];
}

const AnchorNav = ({ sections }: AnchorNavProps) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const navRef = useRef<HTMLElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  /* Detect when the bar leaves its natural position */
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []);

  /* Track active section */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -60% 0px", threshold: 0.1 }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* Invisible sentinel to detect when nav leaves flow */}
      <div ref={sentinelRef} className="h-0 w-full" aria-hidden />

      <div
        className={`fixed left-0 right-0 z-[45] bg-background/95 backdrop-blur-sm border-b border-border/40 transition-all duration-300 ${
          isStuck
            ? "top-[80px] lg:top-[120px] opacity-100 translate-y-0"
            : "top-[80px] lg:top-[120px] opacity-0 -translate-y-2 pointer-events-none"
        }`}
      >
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <nav ref={navRef} className="flex gap-1 overflow-x-auto scrollbar-none py-2">
            {sections.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="relative px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap transition-colors text-muted-foreground hover:text-foreground"
              >
                {activeId === id && (
                  <motion.span
                    layoutId="anchor-pill"
                    className="absolute inset-0 rounded bg-foreground"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                <span className={`relative z-10 ${activeId === id ? "text-background" : ""}`}>
                  {label}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

export default AnchorNav;
